/* eslint-disable react-refresh/only-export-components -- table column utilities plus one JSX cell renderer */
import { Observation } from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import { kgToLbs } from '@/services/patient';
import { formatDate } from '@/services/date';
import { PatientListQueryResultItem } from './types';
import { calculateBMIFromObservations, observationWeightToKg, totalWeightLossPercent } from '@bariendo/lib/bmi';
import {
  BariatricProcedureStage,
  PatientPageFilterOption,
  PATIENT_PAGE_FILTER_OPTIONS,
  BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM,
} from '@bariendo/lib/constants';

/**
 * Re-exported from the shared service so the patient list's existing importers keep working while
 * the patient export consumes it directly from `@/services/patient-fields`.
 */
export { formatProcedureType } from '@/services/patient-fields';

export const PATIENT_LIST_ALL_STAGE_FILTERS = [
  PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_CONSULTATION,
  PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_CONSULTATION,
  PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_PROCEDURE,
  PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_PROCEDURE,
] as const;

export type PatientListAllStageFilter = (typeof PATIENT_LIST_ALL_STAGE_FILTERS)[number];

export function isAllPatientListFilter(
  activeFilter: PatientPageFilterOption
): activeFilter is PatientListAllStageFilter {
  return (PATIENT_LIST_ALL_STAGE_FILTERS as readonly PatientPageFilterOption[]).includes(activeFilter);
}

export const STAGE_TAG_BY_ALL_FILTER: Record<PatientListAllStageFilter, string> = {
  [PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_CONSULTATION]: `${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${BariatricProcedureStage.PRE_CONSULTATION_PAID}`,
  [PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_CONSULTATION]: `${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${BariatricProcedureStage.POST_CONSULTATION}`,
  [PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_PROCEDURE]: `${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${BariatricProcedureStage.PRE_PROCEDURE}`,
  [PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_PROCEDURE]: `${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${BariatricProcedureStage.POST_PROCEDURE}`,
};

const BMI_CELL_DATE_FORMAT = 'MMM D, YYYY';

export function appointmentDateSortingFn(itemA: PatientListQueryResultItem, itemB: PatientListQueryResultItem) {
  const aStart = itemA.relatedAppointment?.[0]?.start;
  const bStart = itemB.relatedAppointment?.[0]?.start;

  if (!aStart || !bStart) {
    return 0;
  }

  const aStartDate = dayjs(aStart);
  const bStartDate = dayjs(bStart);

  if (aStartDate.isBefore(bStartDate)) {
    return 1;
  }

  if (aStartDate.isAfter(bStartDate)) {
    return -1;
  }

  return 0;
}

export function formatPostProcedureTwlDisplay(patient: PatientListQueryResultItem): string {
  const firstWeightObservation = patient.firstWeightObservation?.[0];
  const lastWeightObservation = patient.lastWeightObservation?.[0];

  if (!firstWeightObservation || !lastWeightObservation) {
    return '';
  }

  const startKg = observationWeightToKg(firstWeightObservation);
  const latestKg = observationWeightToKg(lastWeightObservation);

  if (startKg === undefined || latestKg === undefined) {
    return '';
  }

  const startLbs = kgToLbs(startKg);
  const latestLbs = kgToLbs(latestKg);
  if (startLbs <= 0) {
    return '';
  }

  const twlLbs = startLbs - latestLbs;
  const twlPct = totalWeightLossPercent(twlLbs, startLbs);
  return `${twlPct}% (${twlLbs} lbs)`;
}

// BMI follows the weight columns: computed from the first/last weight and the
// latest height. Stored BMI Observations are only written by the charted BMI
// questionnaire, so preferring them froze the column at the last charted value
// once a weight was entered any other way. Stored BMI is the fallback.
export function getStartingBmiValueAndDate(patient: PatientListQueryResultItem): { value: string; dateIso?: string } {
  return bmiValueAndDate(patient.firstWeightObservation?.[0], patient.latestHeight?.[0], patient.startingBmi?.[0]);
}

export function getLatestBmiValueAndDate(patient: PatientListQueryResultItem): { value: string; dateIso?: string } {
  return bmiValueAndDate(patient.lastWeightObservation?.[0], patient.latestHeight?.[0], patient.latestBmi?.[0]);
}

function bmiValueAndDate(
  weight: Observation | undefined,
  height: Observation | undefined,
  stored: Observation | undefined
): { value: string; dateIso?: string } {
  const computed = calculateBMIFromObservations(weight, height);
  if (computed !== undefined) {
    return { value: computed.toFixed(1), dateIso: weight?.effectiveDateTime };
  }
  if (stored?.valueQuantity?.value !== undefined) {
    return { value: stored.valueQuantity.value.toFixed(1), dateIso: stored.effectiveDateTime };
  }
  return { value: '' };
}

export function renderBmiTableCell(value: string, dateIso: string | undefined) {
  if (!value) {
    return '';
  }

  const dateLabel = dateIso ? formatDate(dateIso, BMI_CELL_DATE_FORMAT) : '';

  return (
    <div style={{ textAlign: 'center', lineHeight: 1.15 }}>
      <span>{value}</span>
      {dateLabel ? (
        <div style={{ fontSize: 11, color: 'var(--mantine-color-dimmed, #868e96)' }}>{dateLabel}</div>
      ) : null}
    </div>
  );
}
